import { gql } from "@/gql/__generated__";
import { useSubscription } from "@apollo/client";
import { useMemo } from "react";

const APP_SESSION = gql(`
  subscription APP_SESSION($deployId: ID!, $sessionId: ID!) {
    appSession(deployId: $deployId, sessionId: $sessionId) {
      sessionId
    }
  }
`);

const SUBSCRIPTION_APP_SESSION = gql(`
  subscription SUBSCRIPTION_APP_SESSION($subscriptionId: ID!, $sessionId: ID!) {
    subscriptionAppSession(subscriptionId: $subscriptionId, sessionId: $sessionId) {
      sessionId
    }
  }
`);

const SHARED_APP_SESSION = gql(`
  subscription SHARED_APP_SESSION($sharedUrlId: ID!, $sessionId: ID!, $userId: ID!) {
    sharedAppSession(sharedUrlId: $sharedUrlId, sessionId: $sessionId, userId: $userId) {
      sessionId
    }
  }
`);

type AppSessionType = "organization" | "subscription" | "shared";

interface UseAppSessionSubscriptionProps {
  type: AppSessionType;
  deployId?: string;
  subscriptionId?: string;
  sharedUrlId?: string;
}

export const useAppSessionSubscription = ({
  type,
  deployId,
  subscriptionId,
  sharedUrlId
}: UseAppSessionSubscriptionProps) => {
  const userId = useMemo(() => {
    const storageKey = "numerous_user_id";
    let storedUserId = localStorage.getItem(storageKey);
    if (!storedUserId) {
      storedUserId = crypto.randomUUID();
      localStorage.setItem(storageKey, storedUserId);
    }
    return storedUserId;
  }, []);

  const sessionId = useMemo(() => crypto.randomUUID(), []);

  const skip = (type === "organization" && !deployId) ||
    (type === "subscription" && !subscriptionId) ||
    (type === "shared" && !sharedUrlId);

  let subscription: any;
  let variables: any;

  switch (type) {
    case "organization":
      subscription = APP_SESSION;
      variables = { deployId: deployId!, sessionId };
      break;
    case "subscription":
      subscription = SUBSCRIPTION_APP_SESSION;
      variables = { subscriptionId: subscriptionId!, sessionId };
      break;
    case "shared":
      subscription = SHARED_APP_SESSION;
      variables = { sharedUrlId: sharedUrlId!, sessionId, userId };
      break;
    default:
      throw new Error(`Unknown session type: ${type}`);
  }

  const result = useSubscription(subscription, {
    variables,
    skip,
    fetchPolicy: "no-cache",
    onError: (error) => {
      console.error("App session subscription error:", error);
    }
  });

  return result;
};
